"use client";

import {
  PaletteColor,
  SimplePaletteColorOptions,
  createTheme,
} from "@mui/material";
import { ComponentsOverrides } from "@mui/material/styles";
import {
  chartColorPalette,
  chartColorPaletteTypes,
  colorPalette,
  colorPaletteTypes,
} from "./colors";
import { huHU } from "./locale";

declare module "@mui/material/styles" {
  interface Palette {
    beigeDark: PaletteColor;
    beigeLight: PaletteColor;
    blueBase: PaletteColor;
    blueGray: PaletteColor;
    blueLight: PaletteColor;
    brownDark: PaletteColor;
    darkBase: PaletteColor;
    gray10: PaletteColor;
    gray20: PaletteColor;
    gray30: PaletteColor;
    gray40: PaletteColor;
    gray50: PaletteColor;
    gray60: PaletteColor;
    gray80: PaletteColor;
    gray90: PaletteColor;
    greenBase: PaletteColor;
    greenLight: PaletteColor;
    pinkLight: PaletteColor;
    purpleDark: PaletteColor;
    purpleLight: PaletteColor;
    redLight: PaletteColor;
    redMid: PaletteColor;
    redSmooth: PaletteColor;
    tealBase: PaletteColor;
    turkizLight: PaletteColor;
    white: PaletteColor;
    yellowBase: PaletteColor;
    yellowDark: PaletteColor;
    owGray: PaletteColor;
    fuguOrange: PaletteColor;
  }

  interface PaletteOptions {
    beigeDark: SimplePaletteColorOptions;
    beigeLight: SimplePaletteColorOptions;
    blueBase: SimplePaletteColorOptions;
    blueGray: SimplePaletteColorOptions;
    blueLight: SimplePaletteColorOptions;
    brownDark: SimplePaletteColorOptions;
    darkBase: SimplePaletteColorOptions;
    gray10: SimplePaletteColorOptions;
    gray20: SimplePaletteColorOptions;
    gray30: SimplePaletteColorOptions;
    gray40: SimplePaletteColorOptions;
    gray50: SimplePaletteColorOptions;
    gray60: SimplePaletteColorOptions;
    gray80: SimplePaletteColorOptions;
    gray90: SimplePaletteColorOptions;
    greenBase: SimplePaletteColorOptions;
    greenLight: SimplePaletteColorOptions;
    pinkLight: SimplePaletteColorOptions;
    purpleDark: SimplePaletteColorOptions;
    purpleLight: SimplePaletteColorOptions;
    redLight: SimplePaletteColorOptions;
    redMid: SimplePaletteColorOptions;
    redSmooth: SimplePaletteColorOptions;
    tealBase: SimplePaletteColorOptions;
    turkizLight: SimplePaletteColorOptions;
    white: SimplePaletteColorOptions;
    yellowBase: SimplePaletteColorOptions;
    yellowDark: SimplePaletteColorOptions;
    owGray: SimplePaletteColorOptions;
    fuguOrange: SimplePaletteColorOptions;
  }
}

declare module "@mui/material/styles" {
  interface Theme {
    colors: colorPaletteTypes;
    chartColors: chartColorPaletteTypes;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    colors?: colorPaletteTypes;
    chartColors?: chartColorPaletteTypes;
  }
}

interface StatProps {
  variant?: "light" | "dark";
}

declare module "@mui/material/styles" {
  interface ComponentNameToClassKey {
    OwDrawerHeader: "root";
  }

  interface ComponentsPropsList {
    OwDrawerHeader: Partial<StatProps>;
  }

  interface Components {
    OwDrawerHeader?: {
      styleOverrides?: ComponentsOverrides<Theme>["OwDrawerHeader"];
    };
  }
}

const theme = createTheme();

export const owMui5LightTheme = createTheme(
  {
    colors: colorPalette,
    chartColors: chartColorPalette,
    typography: {
      fontFamily: [
        "Mulish",
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: {
        fontSize: "96px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: "-1.5px",
      },
      h2: {
        fontSize: "60px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h3: {
        fontSize: "48px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h4: {
        fontSize: "34px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        letterSpacing: "0.25px",
      },
      h5: {
        fontSize: "24px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
      },
      h6: {
        fontSize: "20px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "28px",
        letterSpacing: "0.15px",
      },
      body1: {
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "24px",
        letterSpacing: "0.44px",
      },
      body2: {
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "0.25px",
      },
      caption: {
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "16px",
        letterSpacing: "0.4px",
      },
      button: {
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "normal",
        letterSpacing: "0.75px",
        textTransform: "uppercase",
      },
      weeklyEvent: {
        fontSize: "10px",
        lineHeight: "normal",
      },
    },
    palette: {
      text: {
        primary: colorPalette.darkBase,
      },
      primary: theme.palette.augmentColor({
        color: {
          main: colorPalette.primary,
        },
        name: "primary",
      }),
      secondary: theme.palette.augmentColor({
        color: {
          main: "#5E6366",
        },
        name: "primary",
      }),
      success: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenBase,
        },
        name: "succes",
      }),
      error: theme.palette.augmentColor({
        color: {
          main: colorPalette.primary,
        },
        name: "error",
      }),
      warning: theme.palette.augmentColor({
        color: {
          main: colorPalette.fuguOrange,
        },
        name: "warning",
      }),
      info: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray40,
        },
        name: "info",
      }),
      beigeDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.beigeDark,
        },
        name: "beigeDark",
      }),
      beigeLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.beigeLight,
        },
        name: "beigeLight",
      }),
      blueBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueBase,
        },
        name: "blueBase",
      }),
      blueGray: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueGray,
        },
        name: "blueGray",
      }),
      blueLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.blueLight,
        },
        name: "blueLight",
      }),
      brownDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.brownDark,
        },
        name: "brownDark",
      }),
      darkBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.darkBase,
        },
        name: "darkBase",
      }),
      gray10: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray10,
        },
        name: "gray10",
      }),
      gray20: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray20,
        },
        name: "gray20",
      }),
      gray30: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray30,
        },
        name: "gray30",
      }),
      gray40: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray40,
        },
        name: "gray40",
      }),
      gray50: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray50,
        },
        name: "gray50",
      }),
      gray60: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray60,
        },
        name: "gray60",
      }),
      gray80: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray80,
        },
        name: "gray80",
      }),
      gray90: theme.palette.augmentColor({
        color: {
          main: colorPalette.gray90,
        },
        name: "gray90",
      }),
      greenBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenBase,
        },
        name: "greenBase",
      }),
      greenLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.greenLight,
        },
        name: "greenLight",
      }),
      pinkLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.pinkLight,
        },
        name: "pinkLight",
      }),
      purpleDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.purpleDark,
        },
        name: "purpleDark",
      }),
      purpleLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.purpleLight,
        },
        name: "purpleLight",
      }),
      redLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.redLight,
        },
        name: "redLight",
      }),
      redMid: theme.palette.augmentColor({
        color: {
          main: colorPalette.redMid,
        },
        name: "redMid",
      }),
      redSmooth: theme.palette.augmentColor({
        color: {
          main: colorPalette.redSmooth,
        },
        name: "redSmooth",
      }),
      tealBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.tealBase,
        },
        name: "tealBase",
      }),
      turkizLight: theme.palette.augmentColor({
        color: {
          main: colorPalette.turkizLight,
        },
        name: "turkizLight",
      }),
      white: theme.palette.augmentColor({
        color: {
          main: colorPalette.white,
        },
        name: "white",
      }),
      yellowBase: theme.palette.augmentColor({
        color: {
          main: colorPalette.yellowBase,
        },
        name: "yellowBase",
      }),
      yellowDark: theme.palette.augmentColor({
        color: {
          main: colorPalette.yellowDark,
        },
        name: "yellowDark",
      }),
      owGray: theme.palette.augmentColor({
        color: {
          main: colorPalette.owGray,
        },
        name: "owGray",
      }),
      fuguOrange: theme.palette.augmentColor({
        color: {
          main: colorPalette.fuguOrange,
        },
        name: "fuguOrange",
      }),
    },
    components: {
      MuiInputLabel: {
        defaultProps: {
          color: "info",
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          color: "info",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colorPalette.gray40,
              borderWidth: "1px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colorPalette.gray60,
              borderWidth: "1px",
            },
            "&$focused $notchedOutline": {
              borderColor: colorPalette.gray40,
              borderWidth: "1px",
            },
            "&$error": {
              borderColor: colorPalette.primary,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: colorPalette.darkBase,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
          disableElevation: true,
        },
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.variant === "outlined" &&
              ownerState.color === "secondary" && {
                borderColor: colorPalette.gray30,
              }),
            ...(ownerState.variant === "text" &&
              ownerState.color === "primary" && {
                color: colorPalette.darkBase,
                "&:hover": {
                  color: colorPalette.darkBase,
                  backgroundColor: "#0000000a",
                },
              }),
          }),
        },
      },
      OwDrawerHeader: {
        styleOverrides: {
          root: {
            backgroundColor: "#cc0033",
            color: "#ffffff",
          },
        },
      },
    },
  },
  huHU,
);
